/**
 * Prompt Library Submenu Component
 * 
 * A nested dropdown menu that displays prompt library categories and templates.
 * Used in the Threads command bar + menu.
 */

import React, { useState, useMemo } from 'react';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { ChevronRight, BookOpen, Loader2, AlertCircle, Sparkles, User, Settings } from 'lucide-react';
import {
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  useEffectiveTemplates,
  type PromptCategory,
  type PromptTemplate,
} from '@/hooks/usePromptLibrary';
import { cn } from '@/lib/utils';
import { useAuth } from '@/context/AuthContext';

interface PromptLibrarySubmenuProps {
  onSelectPrompt: (template: PromptTemplate) => void;
  onManageMyPrompts: () => void;
}

export function PromptLibrarySubmenu({
  onSelectPrompt,
  onManageMyPrompts,
}: PromptLibrarySubmenuProps) {
  const { user } = useAuth();
  const { data, isLoading, isError } = useEffectiveTemplates(user?.id);
  const [selectedCategoryId, setSelectedCategoryId] = useState<
    number | null | 'my-prompts' | 'uncategorized'
  >(null);

  // Find the selected category
  const selectedCategory = useMemo(() => {
    if (!data?.categories || selectedCategoryId === null) return null;
    if (selectedCategoryId === 'my-prompts') {
      return (
        data.categories.find((c) =>
          c.templates.some((t) => t.scope === 'user'),
        ) || null
      );
    }
    if (selectedCategoryId === 'uncategorized') {
      return data.categories.find((c) => c.id === null) || null;
    }
    return data.categories.find((c) => c.id === selectedCategoryId) || null;
  }, [data?.categories, selectedCategoryId]);

  // Separate system categories and user templates
  const { systemCategories, hasUserPrompts } = useMemo(() => {
    if (!data?.categories) {
      return { systemCategories: [], hasUserPrompts: false };
    }
    
    const hasUser = data.categories.some(c => 
      c.templates.some(t => t.scope === 'user')
    );
    
    // Filter to only show categories with templates
    const system = data.categories.filter(c => c.templates.length > 0);
    
    return { systemCategories: system, hasUserPrompts: hasUser };
  }, [data?.categories]);

  if (isLoading) {
    return (
      <DropdownMenuSub>
        <DropdownMenuSubTrigger className="flex items-center gap-2">
          <BookOpen className="w-4 h-4" />
          <span>Prompt Library</span>
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent className="w-64">
          <div className="flex items-center justify-center py-8 text-gray-500">
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
            <span className="text-sm">Loading prompts...</span>
          </div>
        </DropdownMenuSubContent>
      </DropdownMenuSub>
    );
  }

  if (isError) {
    return (
      <DropdownMenuSub>
        <DropdownMenuSubTrigger className="flex items-center gap-2">
          <BookOpen className="w-4 h-4" />
          <span>Prompt Library</span>
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent className="w-64">
          <div className="flex items-center justify-center py-6 text-gray-500">
            <AlertCircle className="w-4 h-4 text-red-500 mr-2" />
            <span className="text-sm">Failed to load prompts</span>
          </div>
        </DropdownMenuSubContent>
      </DropdownMenuSub>
    );
  }

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger className="flex items-center gap-2">
        <BookOpen className="w-4 h-4 text-purple-600" />
        <span>Prompt Library</span>
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent className="w-[420px] p-0" sideOffset={8}>
        <div className="flex h-[360px]">
          {/* Left side: Categories */}
          <div className="w-[180px] border-r border-gray-100 bg-gray-50/50">
            <div className="px-3 py-2 border-b border-gray-100">
              <span className="text-xs font-medium text-gray-500 uppercase">Categories</span>
            </div>
            <ScrollArea className="h-[320px]">
              <div className="py-1">
                {systemCategories.map((category) => {
                  const userTemplatesInCategory = category.templates.filter(
                    (t) => t.scope === 'user',
                  );
                  const isMyPromptsCategory =
                    userTemplatesInCategory.length > 0 &&
                    category.templates.every((t) => t.scope === 'user');
                  const categoryKey =
                    category.id ??
                    (isMyPromptsCategory ? 'my-prompts' : 'uncategorized');
                  
                  // AR-210 (TC-34.4): plain buttons inside a Radix menu can never take
                  // focus (the menu owns arrow keys and blocks Tab). Each row is now an
                  // unstyled Radix menu item; its keyboard highlight matches the hover look.
                  return (
                    <DropdownMenuPrimitive.Item key={categoryKey} asChild onSelect={(e) => e.preventDefault()}>
                      <button
                        type="button"
                        aria-current={selectedCategoryId === categoryKey ? 'true' : undefined}
                        className={cn(
                          'w-full text-left px-3 py-2 text-sm transition-colors flex items-center justify-between group outline-none',
                          selectedCategoryId === categoryKey
                            ? 'bg-purple-50 text-purple-700'
                            : 'hover:bg-gray-100 data-[highlighted]:bg-gray-100 text-gray-700'
                        )}
                        onClick={() => setSelectedCategoryId(categoryKey)}
                      >
                        <span className="truncate">{category.label}</span>
                        <span className="text-xs text-gray-400 group-hover:text-gray-500">
                          {category.templates.length}
                        </span>
                      </button>
                    </DropdownMenuPrimitive.Item>
                  );
                })}
                
                {/* Manage My Prompts button */}
                <DropdownMenuSeparator className="my-1" />
                <DropdownMenuPrimitive.Item asChild>
                  <button
                    type="button"
                    className="w-full text-left px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 data-[highlighted]:bg-gray-100 outline-none flex items-center gap-2"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onManageMyPrompts();
                    }}
                  >
                    <Settings className="w-3.5 h-3.5" aria-hidden="true" />
                    <span>Manage My Prompts</span>
                  </button>
                </DropdownMenuPrimitive.Item>
              </div>
            </ScrollArea>
          </div>

          {/* Right side: Templates */}
          <div className="flex-1 min-w-0">
            <div className="px-3 py-2 border-b border-gray-100">
              <span className="text-xs font-medium text-gray-500 uppercase">
                {selectedCategory?.label || 'Select a category'}
              </span>
            </div>
            <ScrollArea className="h-[320px]">
              {selectedCategory ? (
                <div className="py-1">
                  {selectedCategory.templates.map((template) => (
                    <DropdownMenuPrimitive.Item key={template.id} asChild>
                    <button
                      type="button"
                      className="w-full text-left px-3 py-2 hover:bg-gray-50 data-[highlighted]:bg-gray-50 outline-none transition-colors border-b border-gray-50 last:border-0"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onSelectPrompt(template);
                      }}
                    >
                      <div className="flex items-start gap-2">
                        <Sparkles className="w-3.5 h-3.5 text-purple-500 mt-0.5 flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-900 truncate">
                              {template.title}
                            </span>
                            {template.scope === 'user' && (
                              <Badge variant="outline" className="text-xs px-1 py-0 h-4 flex-shrink-0">
                                <User className="w-2.5 h-2.5 mr-0.5" />
                                Mine
                              </Badge>
                            )}
                          </div>
                          {template.description && (
                            <p className="text-sm text-gray-500 mt-0.5 line-clamp-2">
                              {template.description}
                            </p>
                          )}
                        </div>
                      </div>
                    </button>
                    </DropdownMenuPrimitive.Item>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-gray-400 px-4">
                  <BookOpen className="w-8 h-8 mb-2 opacity-50" />
                  <p className="text-sm text-center">
                    Select a category to view prompts
                  </p>
                </div>
              )}
            </ScrollArea>
          </div>
        </div>
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
}

export default PromptLibrarySubmenu;

